import React from 'react';
import styled from 'styled-components';
import SEO from '../components/SEO';

const NavbarSpacer = styled.div`
  height: 120px;
  @media (min-width: 768px) {
    height: 140px;
  }
  @media (min-width: 1024px) {
    height: 160px;
  }
`;

const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background: #fff;
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 16px;
  
  @media (min-width: 768px) {
    padding: 60px 24px;
  }
  
  @media (min-width: 1024px) {
    padding: 80px 24px;
  }
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 20px;
  
  @media (min-width: 768px) {
    font-size: 48px;
  }
`;

const LastUpdated = styled.p`
  color: #666;
  font-size: 14px;
  margin-bottom: 40px;
`;

const Section = styled.section`
  margin-bottom: 40px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 16px;
  margin-top: 32px;
  
  @media (min-width: 768px) {
    font-size: 28px;
  }
`;

const Paragraph = styled.p`
  font-size: 16px;
  line-height: 1.8;
  color: #333;
  margin-bottom: 16px;
`;

const List = styled.ul`
  font-size: 16px;
  line-height: 1.8;
  color: #333;
  margin-bottom: 16px;
  padding-left: 24px;
`;

const ListItem = styled.li`
  margin-bottom: 8px;
`;

const HighlightBox = styled.div`
  background: #fff3cd;
  border-left: 4px solid #ffc107;
  padding: 20px;
  margin: 24px 0;
  border-radius: 4px;
`;

const HighlightTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #856404;
`;

const HighlightText = styled.p`
  font-size: 16px;
  line-height: 1.8;
  color: #856404;
  margin-bottom: 8px;
`;

const AddressBox = styled.div`
  background: #f5f5f5;
  padding: 20px;
  margin: 24px 0;
  border-radius: 8px;
  font-family: monospace;
  font-size: 14px;
  line-height: 1.8;
`;

const ContactInfo = styled.div`
  background: #f5f5f5;
  padding: 24px;
  border-radius: 8px;
  margin-top: 32px;
`;

const ContactTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 12px;
`;

const ContactText = styled.p`
  font-size: 16px;
  line-height: 1.8;
  color: #333;
  margin-bottom: 8px;
`;

const ReturnPolicyPage = () => {
  return (
    <PageContainer>
      <SEO 
        title="Return Policy"
        description="Learn about TheMadZone's return policy. 7-day return window, return process, and refund information. Free returns for eligible items."
        keywords="return policy, returns, refunds, exchange policy, TheMadZone returns"
      />
      <NavbarSpacer />
      <ContentWrapper>
        <Title>Return Policy</Title>
        <LastUpdated>Last Updated: {new Date().toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })}</LastUpdated>

        <Section>
          <Paragraph>
            At TheMadZone, we want you to be completely satisfied with your purchase. If you're not happy with your order, you can return it to us within 7 days of the delivery date.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>1. Return Timeframe</SectionTitle>
          
          <Paragraph>
            You have <strong>7 days from the delivery date</strong> to return items for a refund or exchange. The delivery date is the date your order was delivered to you, as confirmed by the shipping carrier.
          </Paragraph>
          
          <HighlightBox>
            <HighlightTitle>Important:</HighlightTitle>
            <HighlightText>
              Returns must be initiated within 7 days of the delivery date. Returns received after this period will not be accepted.
            </HighlightText>
          </HighlightBox>
        </Section>

        <Section>
          <SectionTitle>2. Return Conditions</SectionTitle>
          
          <Paragraph>To be eligible for a return, items must:</Paragraph>
          <List>
            <ListItem>Be unworn, unwashed, and in original condition</ListItem>
            <ListItem>Have all original tags and labels attached</ListItem>
            <ListItem>Be in the original packaging (if applicable)</ListItem>
            <ListItem>Include all accessories or free items that came with the order</ListItem>
          </List>
          
          <Paragraph>
            Items that are damaged, worn, or missing tags may be refused or subject to a reduced refund.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>3. How to Return an Item</SectionTitle>
          
          <Paragraph>
            To return an item, please follow these steps:
          </Paragraph>
          
          <List>
            <ListItem><strong>Step 1:</strong> Contact us at <strong>hello@themadzone.com</strong> with your order number and reason for return</ListItem>
            <ListItem><strong>Step 2:</strong> Pack the item(s) securely in the original packaging (if available)</ListItem>
            <ListItem><strong>Step 3:</strong> Include a note with your <strong>Order ID</strong> clearly written on it</ListItem>
            <ListItem><strong>Step 4:</strong> Send the package to our return address (see below) using a tracked shipping service</ListItem>
            <ListItem><strong>Step 5:</strong> Keep your shipping receipt as proof of return</ListItem>
          </List>
          
          <HighlightBox>
            <HighlightTitle>Required Information:</HighlightTitle>
            <HighlightText>
              <strong>You must include your Order ID</strong> in the return package. Returns without an Order ID may experience delays in processing or may be refused.
            </HighlightText>
          </HighlightBox>
        </Section>

        <Section>
          <SectionTitle>4. Return Shipping Address</SectionTitle>
          
          <Paragraph>
            Please send all returns to:
          </Paragraph>
          
          <AddressBox>
            TheMadZone Returns<br />
            Apartment 2, 45-47 Savile Court<br />
            Hull, England, HU1 3BT<br />
            United Kingdom
          </AddressBox>
          
          <Paragraph>
            <strong>Note:</strong> Please ensure you use a tracked shipping service and keep your shipping receipt. We are not responsible for returns lost in transit.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>5. Return Shipping Costs</SectionTitle>
          
          <Paragraph>
            <strong>You are responsible for the cost of return postage.</strong> Return shipping costs are not refundable unless the item is faulty or we sent you the wrong item.
          </Paragraph>
          
          <Paragraph>
            We recommend using a tracked shipping service to ensure your return reaches us safely. TheMadZone is not responsible for returns lost in transit if you do not use a tracked service.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>6. Refund Process</SectionTitle>
          
          <Paragraph>
            Once we receive your return:
          </Paragraph>
          
          <List>
            <ListItem>We will inspect the returned item(s) within 5-7 business days</ListItem>
            <ListItem>If the return is approved, we will process your refund</ListItem>
            <ListItem>Refunds will be issued to the original payment method</ListItem>
            <ListItem>Refund processing may take 5-10 business days to appear in your account</ListItem>
          </List>
          
          <Paragraph>
            You will receive an email confirmation once your refund has been processed.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>7. Exchanges</SectionTitle>
          
          <Paragraph>
            We currently do not offer direct exchanges. To exchange an item:
          </Paragraph>
          <List>
            <ListItem>Return the original item following our return process</ListItem>
            <ListItem>Place a new order for the item you want</ListItem>
            <ListItem>Once your return is processed, you will receive a refund for the original item</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>8. Non-Returnable Items</SectionTitle>
          
          <Paragraph>The following items cannot be returned:</Paragraph>
          <List>
            <ListItem>Items that have been worn, washed, or damaged</ListItem>
            <ListItem>Items without original tags or labels</ListItem>
            <ListItem>Items returned after the 7-day return period</ListItem>
            <ListItem>Items that are personalized or custom-made</ListItem>
            <ListItem><strong>Gift cards - All gift card purchases are final and non-refundable once the transaction is successful. No refunds will be issued for gift cards under any circumstances.</strong></ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>9. Faulty or Incorrect Items</SectionTitle>
          
          <Paragraph>
            If you receive a faulty item or we sent you the wrong item, please contact us immediately at <strong>hello@themadzone.com</strong>. We will arrange for a free return and full refund, including return shipping costs.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>10. Refund Amount</SectionTitle>
          
          <Paragraph>
            Refunds will be issued for the purchase price of the returned item(s), excluding original shipping costs (unless the item is faulty or incorrect). Any discounts or promotions applied to your original order will be reflected in your refund amount.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>11. Right to Refuse Returns</SectionTitle>
          
          <Paragraph>
            We reserve the right to refuse returns that do not meet our return conditions or are returned after the 7-day period. In such cases, items may be returned to you at your expense.
          </Paragraph>
        </Section>

        <ContactInfo>
          <ContactTitle>Need Help?</ContactTitle>
          <ContactText>
            If you have any questions about returns or need assistance with your return, please contact us:
          </ContactText>
          <ContactText>
            <strong>Email:</strong> hello@themadzone.com
          </ContactText>
          <ContactText>
            <strong>Address:</strong><br />
            Apartment 2, 45-47 Savile Court<br />
            Hull, England, HU1 3BT<br />
            United Kingdom
          </ContactText>
          <ContactText>
            Please include your Order ID in your email for faster service.
          </ContactText>
        </ContactInfo>
      </ContentWrapper>
    </PageContainer>
  );
};

export default ReturnPolicyPage;

